import { createApi } from "@reduxjs/toolkit/query/react";
import Axios from "axios";
import axiosBaseQuery from "../../util/axiosBaseQuery";
import { axiosNewPrivate } from "../../util/axios";
import {
    CreateIssueReportPayload,
    CreateIssueReportResponse,
} from "../types";

// Status transitions of the SUP equipment-check sub-flow (#1604):
// 'withdrawn' on "doch gefunden", 'confirmed' on "wirklich weg/beschädigt".
export interface UpdateIssueReportStatusPayload {
    id: string;
    status: "confirmed" | "withdrawn";
    // Same-booking guard: the backend only patches reports of this order.
    orderId: string;
}

export interface UpdateIssueReportStatusResponse {
    id: string;
    status: "confirmed" | "withdrawn";
}

export const issueReportApiSlice = createApi({
    reducerPath: "issueReportApiSlice",
    baseQuery: axiosBaseQuery(),
    // No query provides a tag here (issue-report creation is a one-way submit
    // with no list to refresh), so this slice declares no tags.
    tagTypes: [],
    endpoints: (builder) => ({
        createNewIssueReport: builder.mutation<
            CreateIssueReportResponse,
            CreateIssueReportPayload
        >({
            query: (data: CreateIssueReportPayload) => {
                const {token, ...rest} = data;
                return {
                    url: `/damage-reports`,
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    data: rest,
                };
            },
        }),
        // Lives on the new-backend (the old Lambda stack is at the
        // CloudFormation resource limit), hence the queryFn bypassing the
        // slice's old-backend base query. axiosNewPrivate carries the
        // X-Api-Key / X-Tenant headers the /app routes expect.
        updateIssueReportStatus: builder.mutation<
            UpdateIssueReportStatusResponse,
            UpdateIssueReportStatusPayload
        >({
            queryFn: async ({ id, status, orderId }) => {
                try {
                    const result = await axiosNewPrivate.patch(
                        `damage-reports/${id}`,
                        { status, orderId },
                    );
                    return { data: result.data as UpdateIssueReportStatusResponse };
                } catch (err) {
                    if (!Axios.isAxiosError(err)) {
                        return { error: { status: undefined, data: String(err) } };
                    }
                    return {
                        error: {
                            status: err.response?.status,
                            data: err.response?.data ?? err.message,
                        },
                    };
                }
            },
        }),
    }),
});

export const {
    useCreateNewIssueReportMutation,
    useUpdateIssueReportStatusMutation,
} = issueReportApiSlice;
